import React, { useEffect } from "react";
import {
    Avatar,
    Box,
    Button,
    Chip,
    Container,
    Divider,
    Stack,
    Typography,
    alpha,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import PricingSection from "./Pricing";
import ServicesSection from "./Services";
import ContactUsSection from "./ContactUs";
import SubTopHeader2 from "./menus/SubTopHeader2";
import FAQ from "./FAQ";
import { SectionHeading } from "./utils";
import DesignerPreviewPanel from "./features/designer/DesignerPreviewPanel";
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import PaletteRoundedIcon from "@mui/icons-material/PaletteRounded";
import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";

const metrics = [
    { label: "Fast setup", value: "< 2 min" },
    { label: "Formats", value: "PNG · SVG · PDF" },
    { label: "Code types", value: "Static + Dynamic" },
];

const trustBadges = ["Editable destinations", "Custom QR designs", "Scan analytics"];

const FEATURE_HIGHLIGHTS = [
    { icon: <BoltRoundedIcon sx={{ fontSize: 20 }} />, title: "Instant Publishing", desc: "Go live in under 2 minutes" },
    { icon: <BarChartRoundedIcon sx={{ fontSize: 20 }} />, title: "Deep Analytics", desc: "Review scan activity by date" },
    { icon: <PaletteRoundedIcon sx={{ fontSize: 20 }} />, title: "Full Customization", desc: "Brand-perfect QR designs" },
    { icon: <VerifiedRoundedIcon sx={{ fontSize: 20 }} />, title: "Dynamic Updates", desc: "Edit destinations anytime" },
];

const HERO_STATS = [
    { label: "Total scans", value: "148.2K", hint: "+18% this month", color: "#5b5df0" },
    { label: "Avg. conversion", value: "34.8%", hint: "3.1× benchmark", color: "#0ea5e9" },
    { label: "Active QR codes", value: "2,400+", hint: "across all plans", color: "#a78bfa" },
    { label: "Countries reached", value: "47", hint: "global reach", color: "#f472b6" },
];

const AVATAR_COLORS = ["#5b5df0", "#0ea5e9", "#a78bfa", "#f472b6"];

const LandingPage = () => {
    const navigate = useNavigate();
    const { search } = useLocation();

    useEffect(() => {
        const params = new URLSearchParams(search);
        const section = params.get("scroll");
        if (!section) return;
        const el = document.getElementById(section);
        if (el) {
            setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 50);
        }
    }, [search]);

    return (
        <>
            <SubTopHeader2 />

            <Box component="main" id="main" sx={{ pb: { xs: 6, md: 10 }, overflow: "hidden" }}>
                <span id="home" />

                {/* ── Hero ── */}
                <Box sx={{ position: "relative" }}>
                    {/* Ambient background decorations */}
                    <Box
                        aria-hidden="true"
                        sx={{
                            position: "absolute",
                            inset: 0,
                            overflow: "hidden",
                            pointerEvents: "none",
                            zIndex: 0,
                        }}
                    >
                        {/* Purple orb — top-left */}
                        <Box sx={{
                            position: "absolute",
                            top: "-8%",
                            left: "-6%",
                            width: { xs: 320, md: 560 },
                            height: { xs: 320, md: 560 },
                            borderRadius: "50%",
                            background: "radial-gradient(circle, rgba(91,93,240,0.22) 0%, transparent 68%)",
                            filter: "blur(18px)",
                        }} />
                        {/* Cyan orb — top-right */}
                        <Box sx={{
                            position: "absolute",
                            top: "2%",
                            right: "-8%",
                            width: { xs: 260, md: 460 },
                            height: { xs: 260, md: 460 },
                            borderRadius: "50%",
                            background: "radial-gradient(circle, rgba(6,182,212,0.18) 0%, transparent 68%)",
                            filter: "blur(18px)",
                        }} />
                        {/* Pink orb — bottom-center */}
                        <Box sx={{
                            position: "absolute",
                            bottom: "-4%",
                            left: "28%",
                            width: { xs: 220, md: 400 },
                            height: { xs: 220, md: 400 },
                            borderRadius: "50%",
                            background: "radial-gradient(circle, rgba(244,114,182,0.12) 0%, transparent 68%)",
                            filter: "blur(22px)",
                        }} />
                        {/* Dot-grid overlay */}
                        <Box sx={{
                            position: "absolute",
                            inset: 0,
                            backgroundImage: "radial-gradient(circle, rgba(91,93,240,0.08) 1px, transparent 1px)",
                            backgroundSize: "30px 30px",
                        }} />
                    </Box>

                    <Container maxWidth="lg" sx={{ pt: { xs: 4, sm: 7, md: 9 }, pb: { xs: 2, md: 4 }, position: "relative", zIndex: 1 }}>
                        <Box
                            sx={{
                                display: "grid",
                                gap: { xs: 4, md: 6 },
                                alignItems: "center",
                                gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1.25fr) minmax(0, 0.75fr)" },
                            }}
                        >
                            {/* ── Left: headline + CTAs ── */}
                            <Stack spacing={{ xs: 2.5, md: 3.5 }} textAlign={{ xs: "center", md: "left" }} alignItems={{ xs: "center", md: "flex-start" }}>
                                {/* Badge */}
                                <Chip
                                    icon={<AutoAwesomeRoundedIcon sx={{ fontSize: "0.95rem !important", color: "primary.main !important" }} />}
                                    label="Modern QR Platform"
                                    sx={{
                                        fontWeight: 700,
                                        borderRadius: 999,
                                        color: "primary.main",
                                        border: `1px solid ${alpha("#5b5df0", 0.32)}`,
                                        bgcolor: alpha("#5b5df0", 0.09),
                                        "& .MuiChip-label": { px: 0.75 },
                                        "& .MuiChip-icon": { ml: 1 },
                                    }}
                                />

                                {/* Headline */}
                                <Typography
                                    component="h1"
                                    sx={{
                                        fontWeight: 900,
                                        maxWidth: 680,
                                        lineHeight: 1.05,
                                        letterSpacing: "-0.034em",
                                        color: "text.primary",
                                        textWrap: "balance",
                                        fontSize: { xs: "2.2rem", sm: "3.1rem", md: "4.1rem" },
                                    }}
                                >
                                    Build{" "}
                                    <Box
                                        component="span"
                                        sx={{
                                            background: "linear-gradient(130deg, #5b5df0 0%, #a78bfa 50%, #22d3ee 100%)",
                                            WebkitBackgroundClip: "text",
                                            WebkitTextFillColor: "transparent",
                                            backgroundClip: "text",
                                        }}
                                    >
                                        Dynamic&nbsp;QR
                                    </Box>{" "}
                                    experiences your customers instantly trust
                                </Typography>

                                <Typography
                                    sx={{
                                        maxWidth: 560,
                                        color: "text.secondary",
                                        lineHeight: 1.72,
                                        fontSize: { xs: "1rem", sm: "1.12rem", md: "1.18rem" },
                                    }}
                                >
                                    Launch menus, campaigns, product journeys, and digital touchpoints
                                    with premium design controls, instant publishing, and analytics
                                    that make every scan count.
                                </Typography>

                                {/* CTA buttons */}
                                <Stack
                                    direction={{ xs: "column", sm: "row" }}
                                    spacing={1.5}
                                    width="100%"
                                    justifyContent={{ xs: "center", md: "flex-start" }}
                                >
                                    <Button
                                        variant="contained"
                                        size="large"
                                        onClick={() => navigate("/auth?mode=signup")}
                                        startIcon={<RocketLaunchRoundedIcon />}
                                        sx={{
                                            minWidth: { sm: 210 },
                                            borderRadius: 999,
                                            fontWeight: 800,
                                            fontSize: "1rem",
                                            py: 1.5,
                                            px: 3.5,
                                            background: "linear-gradient(130deg, #5b5df0 0%, #a78bfa 52%, #22d3ee 100%)",
                                            boxShadow: "0 8px 28px rgba(91,93,240,0.42), 0 2px 8px rgba(167,139,250,0.28)",
                                            "&:hover": {
                                                background: "linear-gradient(130deg, #4749d0 0%, #8b5cf6 52%, #06b6d4 100%)",
                                                boxShadow: "0 14px 38px rgba(91,93,240,0.52), 0 4px 14px rgba(167,139,250,0.36)",
                                                transform: "translateY(-2px)",
                                            },
                                        }}
                                    >
                                        Get Started Free
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="large"
                                        onClick={() => {
                                            window.location.href = "https://qr-flex.com/backend/uploads/QR-FLEX.pdf";
                                        }}
                                        sx={{
                                            minWidth: { sm: 180 },
                                            borderRadius: 999,
                                            fontWeight: 700,
                                            fontSize: "1rem",
                                            py: 1.5,
                                            px: 3,
                                            borderColor: alpha("#5b5df0", 0.4),
                                            bgcolor: alpha("#5b5df0", 0.04),
                                            color: "primary.main",
                                            "&:hover": {
                                                borderColor: "primary.main",
                                                bgcolor: alpha("#5b5df0", 0.1),
                                            },
                                        }}
                                    >
                                        View Presentation
                                    </Button>
                                </Stack>

                                {/* Metric pills */}
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    useFlexGap
                                    flexWrap="wrap"
                                    justifyContent={{ xs: "center", md: "flex-start" }}
                                >
                                    {metrics.map((item) => (
                                        <Chip
                                            key={item.label}
                                            label={`${item.label}: ${item.value}`}
                                            variant="outlined"
                                            sx={{
                                                borderRadius: 999,
                                                fontWeight: 600,
                                                fontSize: "0.8rem",
                                                color: "text.secondary",
                                                borderColor: alpha("#5b5df0", 0.22),
                                                bgcolor: alpha("#ffffff", 0.72),
                                            }}
                                        />
                                    ))}
                                </Stack>
                            </Stack>

                            {/* ── Right: Stats card ── */}
                            <Box
                                sx={{
                                    mx: { xs: "auto", md: 0 },
                                    width: "100%",
                                    maxWidth: { xs: 440, md: "100%" },
                                    borderRadius: 4,
                                    border: `1px solid ${alpha("#5b5df0", 0.16)}`,
                                    background: "linear-gradient(150deg, rgba(255,255,255,0.97) 0%, rgba(244,248,255,0.94) 100%)",
                                    boxShadow: "0 28px 64px rgba(91,93,240,0.13), 0 4px 16px rgba(91,93,240,0.07)",
                                    backdropFilter: "blur(18px)",
                                    p: { xs: 2.5, md: 3 },
                                    position: "relative",
                                    overflow: "hidden",
                                }}
                            >
                                {/* Inner corner glow */}
                                <Box
                                    aria-hidden="true"
                                    sx={{
                                        position: "absolute",
                                        top: -50,
                                        right: -50,
                                        width: 200,
                                        height: 200,
                                        borderRadius: "50%",
                                        background: "radial-gradient(circle, rgba(91,93,240,0.1) 0%, transparent 70%)",
                                        pointerEvents: "none",
                                    }}
                                />

                                <Stack spacing={2.5}>
                                    {/* Card header */}
                                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                        <Stack spacing={0.25}>
                                            <Typography color="text.primary" fontWeight={800} fontSize="1rem" lineHeight={1.2}>
                                                Example dashboard
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Illustrative figures
                                            </Typography>
                                        </Stack>
                                        <Chip
                                            size="small"
                                            label="● Demo"
                                            sx={{
                                                fontWeight: 700,
                                                bgcolor: alpha("#10b981", 0.1),
                                                color: "#065f46",
                                                border: `1px solid ${alpha("#10b981", 0.32)}`,
                                                "& .MuiChip-label": { px: 1.2 },
                                            }}
                                        />
                                    </Stack>

                                    {/* Stats grid — 4 color-coded tiles */}
                                    <Box
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns: "repeat(2, 1fr)",
                                            gap: 1.25,
                                        }}
                                    >
                                        {HERO_STATS.map((stat) => (
                                            <Box
                                                key={stat.label}
                                                sx={{
                                                    p: 1.75,
                                                    borderRadius: 2.5,
                                                    bgcolor: alpha(stat.color, 0.07),
                                                    border: `1px solid ${alpha(stat.color, 0.2)}`,
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    gap: 0.4,
                                                }}
                                            >
                                                <Typography variant="caption" color="text.secondary" fontWeight={600} lineHeight={1.2}>
                                                    {stat.label}
                                                </Typography>
                                                <Typography
                                                    fontWeight={900}
                                                    sx={{
                                                        fontSize: { xs: "1.35rem", md: "1.5rem" },
                                                        color: stat.color,
                                                        lineHeight: 1.1,
                                                    }}
                                                >
                                                    {stat.value}
                                                </Typography>
                                                <Typography sx={{ color: "text.secondary", fontWeight: 500, fontSize: "0.72rem" }}>
                                                    {stat.hint} · Demo data
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>

                                    <Divider sx={{ borderColor: alpha("#5b5df0", 0.1) }} />

                                    {/* Social proof */}
                                    <Stack direction="row" spacing={1.25} alignItems="center">
                                        <Stack direction="row" spacing={-0.7}>
                                            {["A", "M", "S", "R"].map((letter, i) => (
                                                <Avatar
                                                    key={letter}
                                                    sx={{
                                                        width: 32,
                                                        height: 32,
                                                        fontSize: "0.8rem",
                                                        fontWeight: 800,
                                                        color: "#fff",
                                                        bgcolor: AVATAR_COLORS[i],
                                                        border: "2.5px solid #fff",
                                                        boxShadow: `0 2px 8px ${alpha(AVATAR_COLORS[i], 0.4)}`,
                                                    }}
                                                >
                                                    {letter}
                                                </Avatar>
                                            ))}
                                        </Stack>
                                        <Typography sx={{ color: "text.secondary", fontSize: "0.88rem", lineHeight: 1.5 }}>
                                            Trusted by product, retail, and hospitality teams.
                                        </Typography>
                                    </Stack>

                                    {/* Trust badges */}
                                    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                                        {trustBadges.map((badge) => (
                                            <Chip
                                                key={badge}
                                                label={badge}
                                                size="small"
                                                sx={{
                                                    color: "primary.main",
                                                    fontWeight: 600,
                                                    fontSize: "0.72rem",
                                                    bgcolor: alpha("#5b5df0", 0.07),
                                                    border: `1px solid ${alpha("#5b5df0", 0.2)}`,
                                                }}
                                            />
                                        ))}
                                    </Stack>
                                </Stack>
                            </Box>
                        </Box>

                        {/* ── Feature highlights strip ── */}
                        <Box
                            sx={{
                                mt: { xs: 5, md: 8 },
                                display: "grid",
                                gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)" },
                                gap: { xs: 1.5, md: 2 },
                            }}
                        >
                            {FEATURE_HIGHLIGHTS.map((f) => (
                                <Box
                                    key={f.title}
                                    sx={{
                                        p: { xs: 2, md: 2.5 },
                                        borderRadius: 3,
                                        border: `1px solid ${alpha("#5b5df0", 0.13)}`,
                                        bgcolor: alpha("#ffffff", 0.68),
                                        backdropFilter: "blur(10px)",
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 1,
                                        transition: "transform 200ms ease, box-shadow 200ms ease, border-color 200ms ease",
                                        "&:hover": {
                                            transform: "translateY(-4px)",
                                            boxShadow: `0 10px 28px ${alpha("#5b5df0", 0.14)}`,
                                            borderColor: alpha("#5b5df0", 0.3),
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 40,
                                            height: 40,
                                            borderRadius: 2,
                                            bgcolor: alpha("#5b5df0", 0.1),
                                            display: "grid",
                                            placeItems: "center",
                                            color: "primary.main",
                                            flexShrink: 0,
                                        }}
                                    >
                                        {f.icon}
                                    </Box>
                                    <Typography fontWeight={700} fontSize="0.9rem" color="text.primary">
                                        {f.title}
                                    </Typography>
                                    <Typography color="text.secondary" fontSize="0.8rem" lineHeight={1.55}>
                                        {f.desc}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Container>
                </Box>

                {/* ── QR Designer section ── */}
                <Container maxWidth="md" sx={{ pt: { xs: 7, md: 10 } }}>
                    <SectionHeading>Generate Static Codes For Free</SectionHeading>
                    <Typography
                        textAlign="center"
                        color="text.secondary"
                        sx={{ fontSize: { xs: "1rem", sm: "1.1rem" }, mt: 1 }}
                    >
                        A subscription is needed for dynamic QR codes with analytics, editable destinations, and advanced content modules.
                    </Typography>
                </Container>

                <Box sx={{ mt: { xs: 3, md: 4 } }}>
                    <DesignerPreviewPanel />
                </Box>

                <span id="services" />
                <ServicesSection />

                <span id="pricing" />
                <PricingSection />

                <span id="faq" />
                <SectionHeading>Frequently Asked Questions</SectionHeading>
                <FAQ />

                <span id="contact" />
                <ContactUsSection />
            </Box>
        </>
    );
};

export default LandingPage;
